import { ReactElement } from 'react';
// Features
import { SocialMedia } from '@utmb/features/layout/social-media/SocialMedia';
// Design System
import { Col } from '@utmb/design-system/box/Box';
import { Font } from '@utmb/design-system/text/font';
import { Image } from '@utmb/design-system/image/Image';
import { Link } from '@utmb/design-system/link/link';
import { Pin } from '@utmb/design-system/pin/Pin';
// Types
import { BannerView } from '@utmb/types/index';
import { LinkFollowClassEnum } from '@utmb/types/enum/LinkFollowClassEnum';
// Styles
import styles from './page-banner.module.scss';
import { ColorTypesEnum } from '@utmb/types/colors';
import { MicroDataArticle } from '@utmb/types/microdata';
import { useCloudinary } from '@utmb/design-system/use-cloudinary/UseCloudinary';
import { ImageFilterEnum } from '@utmb/types/enum/ImageFilterEnum';
import { CloudinaryTransformEnum } from '@utmb/types/cloudinary-transform.enum';

export interface PageBannerProps extends BannerView {
    button?: {
        label: string;
        href: string;
        color: string;
        bgColor: string;
        followClass?: LinkFollowClassEnum;
    };
    ghostButton?: boolean;
    date?: string;
    dateIso?: string;
    themes?: string[];
    type?: string;
}

const PageBanner = (props: PageBannerProps): ReactElement => {
    const { title, summary, image, button, ghostButton = false, date, dateIso, imageFilter } = props;

    const containerCSS = [styles.container, !image && styles.darker, !image && styles.nopic].filter(Boolean).join(' ');
    const { getImage } = useCloudinary();

    const microdata: MicroDataArticle | null = dateIso
        ? {
              '@context': 'https://schema.org',
              '@type': 'NewsArticle',
              headline: title,
              datePublished: dateIso,
              image: [getImage(image.publicId, { maxWidth: 1920 })],
          }
        : null;

    return (
        <Col className={containerCSS}>
            {/* microdata */}
            {microdata && <script type="application/ld+json" suppressHydrationWarning dangerouslySetInnerHTML={{ __html: JSON.stringify(microdata) }} />}
            {image && (
                <Pin.Cover xy={{ zIndex: -1 }}>
                    <Image
                        objectFit="cover"
                        layout="fill"
                        image={image}
                        className={imageFilter === ImageFilterEnum.SIMPLE_ALPHA ? styles.filtered_img : undefined}
                        addOptions={imageFilter === ImageFilterEnum.EPIC ? [CloudinaryTransformEnum.EPIC] : undefined}
                    />
                </Pin.Cover>
            )}
            {date && (
                <Font.FuturaBook mobile="13" desktop="16" className={styles.date} as="div">
                    {date}
                </Font.FuturaBook>
            )}
            {title && (
                <Font.FuturaMedium mobile="32" desktop="32" className={styles.title} as="h1">
                    {title}
                </Font.FuturaMedium>
            )}
            {summary && (
                <Font.FuturaMedium mobile="16" desktop="18" className={styles.summary} as="div">
                    <div dangerouslySetInnerHTML={{ __html: summary }} />
                </Font.FuturaMedium>
            )}
            {!ghostButton && button && (
                <Link.Button
                    composeClassName={styles.button}
                    label={button.label}
                    href={button.href}
                    followClassName={`${button.followClass}-ga`}
                    color={ColorTypesEnum.PRIMARY}
                    bgcolor={ColorTypesEnum.SECONDARY}
                ></Link.Button>
            )}
            {ghostButton && button && (
                <Link.Default
                    composeClassName={styles.button}
                    label={button.label}
                    href={button.href}
                    followClassName={`${button.followClass}-ga`}
                ></Link.Default>
            )}

            <Pin xy={{ top: '50%', right: '30px', transform: 'translateY(-50%)' }}>
                <SocialMedia.Vertical className="landscape-only" colorTheme={image ? ColorTypesEnum.WHITE : ColorTypesEnum.PRIMARY} />
            </Pin>
        </Col>
    );
};

export default PageBanner;
